import {
  BankOutlined,
  CopyOutlined,
  EditOutlined,
  PlusOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import {
  Row,
  Col,
  App,
  Button,
  Card,
  Drawer,
  Empty,
  Flex,
  Form,
  Grid,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { applyViolations, errorMessage, useApi, useDownload } from '../../api/client';
import { keys, useApiMutation, useCompanies, useSites, useTenantExports } from '../../api/hooks';
import type { Company, Site, TenantExport } from '../../api/types';
import { ExportButton } from '../../components/ExportButton';
import { PageHeader } from '../../components/PageHeader';
import { useSiteContext } from '../../context/SiteContext';
import {
  CompanyFields,
  NEW_SITE_DEFAULTS,
  SiteFields,
  companyPayload,
  type CompanyValues,
  type SiteValues,
} from './OrganisationForms';
import { SiteCopyModal, SiteCopySection, copySummaryText } from './SiteCopy';
import dayjs from 'dayjs';
import { UsagePanel } from '../../components/UsagePanel';
import { formatDateTime } from '../../format';

/**
 * Companies and sites of the tenant (FR-PAR-01, FR-PAR-02); a new site may start from the
 * configuration of another one (FR-PAR-47).
 */
export function OrganisationPage() {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const api = useApi();
  const { me, setSiteId, can } = useSiteContext();
  const companies = useCompanies();
  const sites = useSites();
  const [company, setCompany] = useState<Company | 'new'>();
  const [newSiteFor, setNewSiteFor] = useState<string>();
  const [copyTo, setCopyTo] = useState<Site>();
  const { message } = App.useApp();
  const [companyForm] = Form.useForm<CompanyValues>();
  const [siteForm] = Form.useForm<SiteValues>();

  useEffect(() => {
    if (company === 'new') companyForm.setFieldsValue({ legalName: '', country: 'MA' });
    else if (company)
      companyForm.setFieldsValue({
        legalName: company.legalName,
        ice: company.ice ?? undefined,
        rc: company.rc ?? undefined,
        if: company.ifNumber ?? undefined,
        address: company.address ?? undefined,
        city: company.city ?? undefined,
        country: company.country,
        ownTransport: Boolean(company.ownTransportAuthorization),
        ownTransportAuthorization: company.ownTransportAuthorization
          ? {
              number: company.ownTransportAuthorization.number,
              issuingAuthority: company.ownTransportAuthorization.issuingAuthority,
              validity: [
                dayjs(company.ownTransportAuthorization.validFrom),
                dayjs(company.ownTransportAuthorization.validTo),
              ],
              notes: company.ownTransportAuthorization.notes,
            }
          : undefined,
      });
  }, [company, companyForm]);
  useEffect(() => {
    if (newSiteFor) siteForm.setFieldsValue({ ...NEW_SITE_DEFAULTS, companyId: newSiteFor });
  }, [newSiteFor, siteForm]);

  const saveCompany = useApiMutation(
    (v: CompanyValues) =>
      company === 'new'
        ? api('/companies', { method: 'POST', body: JSON.stringify(companyPayload(v)) })
        : api(`/companies/${(company as Company).id}`, {
            method: 'PATCH',
            body: JSON.stringify(companyPayload(v)),
          }),
    { invalidate: [keys.companies, keys.me] },
  );
  const createSite = useApiMutation(
    ({ copyFrom, ...v }: SiteValues) =>
      api<Site>('/sites', {
        method: 'POST',
        body: JSON.stringify(copyFrom?.sourceSiteId ? { ...v, copyFrom } : v),
      }),
    { invalidate: [keys.sites, keys.me], success: false, silentError: true },
  );
  const siteName = (id: string) => sites.data?.find((s) => s.id === id)?.name;

  return (
    <>
      <PageHeader
        icon={<BankOutlined />}
        title={t('organisation.title')}
        subtitle={t('organisation.subtitle', { tenant: me?.tenant.name })}
        crumbs={[{ title: t('nav.administration') }, { title: t('organisation.title') }]}
        actions={
          <>
            <ExportButton<{ site: Site; company?: Company }>
              fileName={`societes-et-sites-${me?.tenant.name ?? ''}`}
              rows={(sites.data ?? []).map((site) => ({
                site,
                company: companies.data?.find((c) => c.id === site.companyId),
              }))}
              columns={[
                {
                  header: t('organisation.company'),
                  value: (r) => r.company?.legalName,
                  width: 30,
                },
                { header: 'ICE', value: (r) => r.company?.ice, width: 18 },
                { header: 'RC', value: (r) => r.company?.rc },
                { header: 'IF', value: (r) => r.company?.ifNumber },
                { header: t('organisation.site'), value: (r) => r.site.name, width: 28 },
                { header: t('common.address'), value: (r) => r.site.address, width: 30 },
                { header: t('common.city'), value: (r) => r.site.city },
                { header: t('sitePrefs.fields.region'), value: (r) => r.site.region },
                {
                  header: t('organisation.responsable'),
                  value: (r) => r.site.responsableDechets?.name,
                  width: 22,
                },
                {
                  header: t('common.email'),
                  value: (r) => r.site.responsableDechets?.email,
                  width: 26,
                },
                {
                  header: t('sitePrefs.fields.mask'),
                  value: (r) => r.site.numberingMask,
                  width: 20,
                },
                {
                  header: t('common.status'),
                  value: (r) => (r.site.active ? t('common.active') : t('common.inactive')),
                },
              ]}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCompany('new')}>
              {t('organisation.newCompany')}
            </Button>
          </>
        }
      />
      {(companies.data ?? []).length === 0 && !companies.isLoading && (
        <Empty description={t('organisation.empty')} />
      )}
      <Flex vertical gap={16}>
        {(companies.data ?? []).map((c) => {
          const own = (sites.data ?? []).filter((s) => s.companyId === c.id);
          return (
            <Card
              key={c.id}
              title={
                <Flex gap={10} align="center" style={{ minWidth: 0 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {c.legalName}
                  </span>
                  {c.ice && screens.md && <Tag variant="filled">ICE {c.ice}</Tag>}
                </Flex>
              }
              extra={
                <Space>
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => setCompany(c)}
                    aria-label={t('organisation.editCompany')}
                  >
                    {screens.sm && t('common.edit')}
                  </Button>
                  <Button
                    type="primary"
                    ghost
                    icon={<PlusOutlined />}
                    onClick={() => setNewSiteFor(c.id)}
                    aria-label={t('organisation.newSite')}
                  >
                    {screens.sm && t('organisation.newSite')}
                  </Button>
                </Space>
              }
              styles={{ body: { padding: 0 } }}
            >
              <Table<Site>
                rowKey="id"
                scroll={{ x: 'max-content' }}
                dataSource={own}
                pagination={false}
                locale={{ emptyText: t('organisation.noSite') }}
                columns={[
                  {
                    title: t('organisation.site'),
                    dataIndex: 'name',
                    minWidth: 200,
                    render: (n: string, s) => (
                      <Typography.Link onClick={() => setSiteId(s.id)}>{n}</Typography.Link>
                    ),
                  },
                  { title: t('common.city'), dataIndex: 'city' },
                  { title: t('sitePrefs.fields.region'), dataIndex: 'region' },
                  {
                    title: t('organisation.responsable'),
                    render: (_, s) => s.responsableDechets?.name,
                  },
                  {
                    title: t('sitePrefs.fields.mask'),
                    dataIndex: 'numberingMask',
                    render: (m: string) => <span className="wm-code">{m}</span>,
                  },
                  {
                    title: t('common.status'),
                    dataIndex: 'active',
                    render: (a: boolean) =>
                      a ? (
                        <Tag color="success">{t('common.active')}</Tag>
                      ) : (
                        <Tag>{t('common.inactive')}</Tag>
                      ),
                  },
                  {
                    key: 'copy',
                    align: 'end',
                    render: (_, s) =>
                      (sites.data ?? []).length > 1 && (
                        <Tooltip title={t('siteCopy.action')}>
                          <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            aria-label={t('siteCopy.action')}
                            onClick={() => setCopyTo(s)}
                          />
                        </Tooltip>
                      ),
                  },
                ]}
              />
            </Card>
          );
        })}
      </Flex>

      {can.administerTenant && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} xl={14}>
            <Card title={t('usage.title')}>
              <UsagePanel path="/usage" />
            </Card>
          </Col>
          <Col xs={24} xl={10}>
            <TenantExportCard />
          </Col>
        </Row>
      )}

      <Drawer
        open={Boolean(company)}
        onClose={() => setCompany(undefined)}
        size={520}
        title={company === 'new' ? t('organisation.newCompany') : t('organisation.editCompany')}
        destroyOnHidden
        extra={
          <Space>
            <Button onClick={() => setCompany(undefined)}>{t('common.cancel')}</Button>
            <Button
              type="primary"
              loading={saveCompany.isPending}
              onClick={() =>
                void companyForm
                  .validateFields()
                  .then((v) => saveCompany.mutateAsync(v))
                  .then(() => setCompany(undefined))
                  .catch((e) => applyViolations(companyForm, e))
              }
            >
              {t('common.save')}
            </Button>
          </Space>
        }
      >
        <Form form={companyForm} layout="vertical">
          <CompanyFields />
        </Form>
      </Drawer>

      <Drawer
        open={Boolean(newSiteFor)}
        onClose={() => setNewSiteFor(undefined)}
        size={560}
        title={t('organisation.newSite')}
        destroyOnHidden
        extra={
          <Space>
            <Button onClick={() => setNewSiteFor(undefined)}>{t('common.cancel')}</Button>
            <Button
              type="primary"
              loading={createSite.isPending}
              onClick={() =>
                void siteForm
                  .validateFields()
                  .then((v) => createSite.mutateAsync(v))
                  .then((created) => {
                    message.success(
                      created.copied
                        ? t('siteCopy.created', {
                            site: created.name,
                            source: siteName(created.copied.sourceSiteId),
                            details: copySummaryText(t, created.copied),
                          })
                        : t('organisation.siteCreated'),
                    );
                    setNewSiteFor(undefined);
                  })
                  .catch((e) => {
                    if (e?.errorFields) return;
                    if (!applyViolations(siteForm, e)) message.error(errorMessage(e));
                  })
              }
            >
              {t('common.create')}
            </Button>
          </Space>
        }
      >
        <Form form={siteForm} layout="vertical">
          <SiteFields />
          <SiteCopySection sites={sites.data ?? []} companies={companies.data ?? []} />
        </Form>
      </Drawer>
      {copyTo && (
        <SiteCopyModal
          target={copyTo}
          sites={sites.data ?? []}
          companies={companies.data ?? []}
          onClose={() => setCopyTo(undefined)}
        />
      )}
    </>
  );
}

/** FR-TRV-18: the full data export, built on request and kept as a download. */
export function TenantExportCard() {
  const { t } = useTranslation();
  const api = useApi();
  const { message } = App.useApp();
  const download = useDownload();
  const exports = useTenantExports();
  const request = useApiMutation(() => api<TenantExport>('/exports', { method: 'POST' }), {
    invalidate: [keys.exports],
    success: t('tenantExport.ready'),
  });
  return (
    <Card
      title={t('tenantExport.title')}
      extra={
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          loading={request.isPending}
          onClick={() => request.mutate()}
        >
          {t('tenantExport.request')}
        </Button>
      }
      styles={{ body: { padding: 0 } }}
    >
      <Typography.Paragraph type="secondary" style={{ padding: '12px 16px 0', marginBottom: 0 }}>
        {t('tenantExport.hint')}
      </Typography.Paragraph>
      <Table<TenantExport>
        rowKey="id"
        size="small"
        loading={exports.isLoading}
        dataSource={exports.data ?? []}
        pagination={false}
        locale={{ emptyText: t('tenantExport.empty') }}
        columns={[
          {
            title: t('tenantExport.columns.requestedAt'),
            render: (_, e) => (
              <div>
                <div>{formatDateTime(e.requestedAt)}</div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {e.requesterName}
                </Typography.Text>
              </div>
            ),
          },
          {
            title: t('tenantExport.columns.content'),
            render: (_, e) =>
              t('tenantExport.content', {
                tables: e.tableCount,
                rows: e.rowCount,
                size: `${Math.round(e.sizeBytes / 1024)} Ko`,
              }),
          },
          {
            width: 60,
            render: (_, e) => (
              <Button
                size="small"
                type="text"
                icon={<DownloadOutlined />}
                aria-label={t('common.download')}
                onClick={() =>
                  download(`/exports/${e.id}/file`, e.fileName).catch((err) =>
                    message.error(errorMessage(err)),
                  )
                }
              />
            ),
          },
        ]}
      />
    </Card>
  );
}
